import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { toast } from "sonner";
import { LEAD_STATUSES, LEAD_SOURCES } from "./leadMeta";

export const EMPTY_LEAD = { name: "", phone: "", email: "", address: "", suburb: "", postcode: "", source: "", notes: "", status: "new", preferred_channel: "sms" };
const L = ({ children }) => <Label className="label-caps text-muted-foreground">{children}</Label>;
const sel = "w-full border border-border/70 rounded-sm px-3 py-2 bg-background text-sm outline-none focus:border-brand";

export const leadPayload = (l) => Object.fromEntries(Object.keys(EMPTY_LEAD).map((k) => [k, l[k] ?? EMPTY_LEAD[k]]));

export default function LeadFormDialog({ lead, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_LEAD);
  const editing = lead?.id;
  useEffect(() => { if (lead) setForm({ ...EMPTY_LEAD, ...lead }); }, [lead]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      const payload = leadPayload(form);
      const { data } = editing ? await api.put(`/leads/${editing}`, payload) : await api.post("/leads", payload);
      toast.success(editing ? "Lead updated" : "Lead added");
      onSaved(data, !editing); onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="lead-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit Lead" : "New Lead"}</DialogTitle>
          <DialogDescription>Save the enquiry, then book their free walk-through.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2"><L>Name *</L><Input value={form.name} onChange={set("name")} autoFocus data-testid="lead-name" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><L>Mobile</L><Input value={form.phone} onChange={set("phone")} placeholder="0412 345 678" data-testid="lead-phone" /></div>
            <div className="space-y-2"><L>Email</L><Input type="email" value={form.email} onChange={set("email")} data-testid="lead-email" /></div>
          </div>
          <div className="space-y-2">
            <L>Address</L>
            <AddressAutocomplete value={form.address} testid="lead-address" onChange={(v) => setForm((f) => ({ ...f, address: v }))}
              onSelect={(p) => setForm((f) => ({ ...f, address: p.address || f.address, suburb: p.suburb || f.suburb, postcode: p.postcode || f.postcode }))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><L>Suburb</L><Input value={form.suburb} onChange={set("suburb")} data-testid="lead-suburb" /></div>
            <div className="space-y-2"><L>Postcode</L><Input value={form.postcode} onChange={set("postcode")} data-testid="lead-postcode" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><L>How they found you</L>
              <Input list="lead-sources" value={form.source} onChange={set("source")} placeholder="e.g. Google" data-testid="lead-source" />
              <datalist id="lead-sources">{LEAD_SOURCES.map((s) => <option key={s} value={s} />)}</datalist>
            </div>
            <div className="space-y-2"><L>Status</L>
              <select value={form.status} onChange={set("status")} className={sel} data-testid="lead-status">
                {LEAD_STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-2"><L>Confirmations & reminders by</L>
            <select value={form.preferred_channel} onChange={set("preferred_channel")} className={sel} data-testid="lead-channel">
              <option value="sms">SMS only</option><option value="email">Email only</option><option value="both">SMS &amp; Email</option>
            </select>
          </div>
          <div className="space-y-2"><L>Notes</L><Textarea value={form.notes} onChange={set("notes")} placeholder="What are they after? Bedrooms, pets, frequency…" data-testid="lead-notes" /></div>
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="lead-cancel">Cancel</Button>
          <Button onClick={save} className="rounded-sm" data-testid="lead-save">Save lead</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
